import {
  rapcoin1001,
  rapcoin2001,
  rapcoin3001,
  rapcoin5001,
  rapcoin501,
} from '../assets/bazzar';
import { axios_adapter } from '../network/axios_adapter';
import ENDPOINTS from '../network/endpoints';
import { getTotalXp } from '../storage/userProfileStorage';
import { formatXpCost } from '../utils/formatAbbreviatedXp';

export const BAZAAR_IAP_TYPES = {
  rapcoin: 'rapcoinBundle',
  rapgem: 'gems',
  'vip-room': 'roomPack',
  passes: 'pass',
};

const RAPCOIN_ASSETS = [
  { coinImage: rapcoin501, coinImageWidth: 53, coinImageHeight: 50 },
  { coinImage: rapcoin1001, coinImageWidth: 60, coinImageHeight: 50 },
  { coinImage: rapcoin2001, coinImageWidth: 50, coinImageHeight: 50 },
  { coinImage: rapcoin3001, coinImageWidth: 50, coinImageHeight: 50 },
  { coinImage: rapcoin5001, coinImageWidth: 50, coinImageHeight: 50 },
];

const PASS_INTERVAL_STYLES = {
  daily: { tone: '#2c0041', toneText: '#ae00ff', toneBorder: 'rgba(174, 0, 255, 0)' },
  weekly: { tone: '#41003c', toneText: '#ff00eb', toneBorder: 'rgba(255, 0, 235, 0)' },
  monthly: { tone: '#3c4100', toneText: '#e6f900', toneBorder: 'rgba(230, 249, 0, 0)' },
};

const PASS_PRICE_SUFFIX = {
  daily: '/day',
  weekly: '/week',
  monthly: '/month',
};

export const PASS_INTERVAL_ORDER = { daily: 0, weekly: 1, monthly: 2 };
export const GEM_PASS_INTERVALS = new Set(['daily', 'weekly', 'monthly']);

const VIP_PASS_TONES = ['#003341', '#004d0d', '#4a2300'];
const VIP_PASS_TONES_TEXT = ['#009dc9', '#07b04f', '#FF8900'];
const VIP_PASS_TONES_BORDER = ['#009dc9', '#07b04f', '#FF8900'];

function getItemIapType(item) {
  return pickString(item, ['type', 'iapType', 'productType', 'packageType'], '').toLowerCase();
}

export function isGemPassItem(item) {
  if (item?.passKind === 'gem') return true;
  if (item?.passKind === 'vip') return false;
  return getItemIapType(item?.raw ?? item) === 'pass';
}

export function isVipPassItem(item) {
  if (item?.passKind === 'vip') return true;
  if (item?.passKind === 'gem') return false;
  const iapType = getItemIapType(item?.raw ?? item);
  return Boolean(iapType) && iapType !== 'pass';
}

const SOURCE_IMAGE_TONE = {
  pvr: 'cinema',
  haldirams: 'food',
  myntra: 'fashion',
  amazon: 'fashion',
  flipkart: 'fashion',
  zomato: 'food',
};

function formatRedeemedAtLabel(value) {
  if (value == null || value === '') return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).trim();

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function inferRewardMeta(item = {}) {
  const title = pickString(item, ['title', 'rewardTitle', 'name'], '').toLowerCase();
  const source = pickString(item, ['source', 'brand', 'displayName'], '').toLowerCase();
  const haystack = `${title} ${source}`;

  if (haystack.includes('zomato') || haystack.includes('swiggy') || haystack.includes('haldiram')) {
    return { brand: 'Zomato', imageTone: 'food' };
  }
  if (haystack.includes('pvr')) {
    return { brand: 'PVR', imageTone: 'cinema' };
  }
  if (haystack.includes('flipkart')) {
    return { brand: 'Flipkart', imageTone: 'fashion' };
  }
  if (haystack.includes('myntra')) {
    return { brand: 'Myntra', imageTone: 'fashion' };
  }
  if (haystack.includes('amazon')) {
    return { brand: 'Amazon', imageTone: 'fashion' };
  }

  const tone = SOURCE_IMAGE_TONE[source] || 'fashion';
  return {
    brand: pickString(item, ['displayName', 'source', 'brand'], 'Voucher'),
    imageTone: tone,
  };
}

function formatValidTillLabel(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string' && /^valid till/i.test(value.trim())) {
    return value.trim();
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return `Valid Till : ${String(value).trim()}`;
  }

  const formatted = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `Valid Till : ${formatted}`;
}

function getFeatureTitle(feature) {
  if (typeof feature === 'string') return feature.trim();
  if (feature && typeof feature === 'object') {
    return pickString(feature, ['title', 'text', 'label', 'description']);
  }
  return '';
}

function inferPassInterval(item) {
  const name = pickString(item, ['displayName', 'title', 'name'], '').toLowerCase();
  if (name.includes('daily')) return 'daily';
  if (name.includes('weekly')) return 'weekly';
  if (name.includes('monthly')) return 'monthly';

  const productId = pickString(item, ['androidStoreProductId', 'appleStoreProductId'], '').toLowerCase();
  if (productId.includes('daily')) return 'daily';
  if (productId.includes('weekly')) return 'weekly';
  if (productId.includes('monthly')) return 'monthly';

  const units = String(item?.duration?.units || '').toLowerCase();
  const value = Number(item?.duration?.value);
  if (units === 'days' && value === 1) return 'daily';
  if (units === 'days' && value === 7) return 'weekly';
  if (units === 'days' && value >= 28) return 'monthly';
  if (units === 'months') return 'monthly';

  return pickString(item, ['interval', 'billingInterval', 'packageInterval'], 'monthly').toLowerCase();
}

function parseGemPassBenefits(features, pkg) {
  let swapBenefit = '';
  let boostBenefit = '';

  for (const feature of features) {
    const text = getFeatureTitle(feature);
    if (!text) continue;

    if (/swap/i.test(text)) {
      swapBenefit = text;
      continue;
    }

    if (/boost/i.test(text)) {
      boostBenefit = text;
    }
  }

  return {
    swapBenefit: swapBenefit || (pkg.unlimitedSwap === false ? '' : 'Unlimited Swap'),
    boostBenefit: boostBenefit || (pkg.unlimitedBoost === false ? '' : 'Unlimited Boost'),
  };
}

function formatVipGemsDetail(pkg, gemsTitle) {
  if (pkg.gems != null && Number(pkg.gems) >= 999999) return '(Unlimited)';

  if (gemsTitle) {
    if (/unlimited/i.test(gemsTitle)) return '(Unlimited)';
    const match = gemsTitle.match(/\(([^)]+)\)/);
    if (match) return `(${match[1]})`;
    const amount = gemsTitle.replace(/^rap\s*gems?\s*/i, '').trim();
    const numeric = amount.match(/[\d,]+/)?.[0];
    if (numeric) return `(${numeric})`;
  }

  if (pkg.gems != null && pkg.gems !== '') return `(${pkg.gems})`;
  return '';
}

function resolveVipRoomAccess(featureTitles, pkg) {
  const roomFeatures = featureTitles.filter((text) => /room/i.test(text));
  if (roomFeatures.length) {
    return roomFeatures.find((text) => /family/i.test(text)) || roomFeatures[0];
  }

  if (!Array.isArray(pkg.rooms) || !pkg.rooms.length) return '';

  const roomLabels = {
    familyRoom: 'Family',
    kittyRoom: 'Kitty',
    corporateRoom: 'Corporate',
  };
  const primary =
    pkg.rooms.find((room) => room.type === 'familyRoom') ||
    pkg.rooms.find((room) => room.type === 'kittyRoom') ||
    pkg.rooms[0];

  const label = roomLabels[primary.type] || 'Room';
  return `${primary.roomCount} ${label} Room access`;
}

function parseVipPassBenefits(features, pkg) {
  const featureTitles = features.map(getFeatureTitle).filter(Boolean);
  let points = featureTitles.find((text) => /coin/i.test(text)) || '';
  const gemsTitle = featureTitles.find((text) => /rap\s*gem/i.test(text)) || '';
  let gemsDetail = formatVipGemsDetail(pkg, gemsTitle);
  let roomAccess = resolveVipRoomAccess(featureTitles, pkg);

  if (!points && pkg.rapCoins != null) {
    points = `${pkg.rapCoins} Rap Coins`;
  } else if (!points && pkg.coins) {
    points = `${pkg.coins} Rap Coins`;
  }
  if (!gemsDetail) {
    gemsDetail = formatVipGemsDetail(pkg, '');
  }
  if (!roomAccess) {
    roomAccess = resolveVipRoomAccess([], pkg);
  }

  return { points, gemsDetail, roomAccess };
}

function buildPassValidity(item, pkg, validityDays, displayDuration) {
  if (displayDuration) return `Validity : ${displayDuration}`;
  if (validityDays) return `Validity : ${validityDays} Days`;
  return pickString(item, ['description'], 'Validity : 30 Days');
}

function buildItemGradientCss(gradient, fallback = '#2c0041') {
  if (!gradient || typeof gradient !== 'object') {
    return fallback;
  }

  const startColor = gradient.startColor || gradient.start || fallback;
  const endColor = gradient.endColor || gradient.end || startColor;
  const type = String(gradient.type || 'radial').toLowerCase();
  const angle = Number(gradient.angle);
  const resolvedAngle = Number.isFinite(angle) ? angle : 90;

  if (type === 'linear') {
    return `linear-gradient(${resolvedAngle}deg, ${startColor} 0%, ${endColor} 100%)`;
  }

  return `radial-gradient(circle at 50% 50%, ${startColor} 63.46%, ${endColor} 100%)`;
}

function resolvePassItemStyle(item, fallbackStyle = {}) {
  const textColour = pickString(item, ['textColour', 'textColor'], '');
  const headerGradient = item?.gradient
    ? buildItemGradientCss(item.gradient, fallbackStyle.tone)
    : fallbackStyle.tone;
  const resolvedTextColour = textColour || fallbackStyle.toneText;

  return {
    headerGradient,
    tone: headerGradient,
    toneText: resolvedTextColour,
    toneBorder: textColour || fallbackStyle.toneText || fallbackStyle.toneBorder,
  };
}

function mapGemPassItem(item, index) {
  const title = pickString(item, ['displayName', 'title', 'name', 'packageName'], `PASS ${index + 1}`).toUpperCase();
  const pkg = getPackage(item);
  const features = Array.isArray(item?.displayFeatures) ? item.displayFeatures : [];
  const validityDays =
    pkg.validityDays ??
    pkg.days ??
    item?.duration?.value ??
    pickNumber(item, ['validityDays', 'validity', 'durationDays']);
  const displayDuration = pickString(item, ['displayDuration']);
  const { swapBenefit, boostBenefit } = parseGemPassBenefits(features, pkg);
  const displayPrice = pickString(item, ['displayPrice']);
  const { price: resolvedPrice, originalPrice } = resolveIapPrices(item);
  const price = resolvedPrice || displayPrice;
  const interval = inferPassInterval(item);
  const intervalStyle = PASS_INTERVAL_STYLES[interval] || PASS_INTERVAL_STYLES.monthly;
  const passStyle = resolvePassItemStyle(item, intervalStyle);
  const priceSuffix = PASS_PRICE_SUFFIX[interval] || '';
  const priceLabel = appendPassPriceSuffix(price, priceSuffix);
  const originalPriceLabel = appendPassPriceSuffix(originalPrice, priceSuffix);

  return {
    id: getIapId(item, index),
    passKind: 'gem',
    title,
    validity: buildPassValidity(item, pkg, validityDays, displayDuration),
    swapBenefit,
    boostBenefit,
    price,
    priceLabel,
    originalPrice: originalPriceLabel,
    headerGradient: passStyle.headerGradient,
    tone: passStyle.tone,
    toneText: passStyle.toneText,
    toneBorder: passStyle.toneBorder,
    interval,
    productId: item?.androidStoreProductId,
    raw: item,
  };
}

function mapVipPassItem(item, index) {
  const title = pickString(item, ['displayName', 'title', 'name', 'packageName'], `VIP PASS ${index + 1}`).toUpperCase();
  const pkg = getPackage(item);
  const features = Array.isArray(item?.displayFeatures) ? item.displayFeatures : [];
  const validityDays =
    pkg.validityDays ??
    pkg.days ??
    item?.duration?.value ??
    pickNumber(item, ['validityDays', 'validity', 'durationDays']);
  const displayDuration = pickString(item, ['displayDuration']);
  const { points, gemsDetail, roomAccess } = parseVipPassBenefits(features, pkg);
  const displayPrice = pickString(item, ['displayPrice']);
  const { price: resolvedPrice, originalPrice } = resolveIapPrices(item);
  const price = resolvedPrice || displayPrice;
  const interval = pickString(item, ['interval', 'billingInterval', 'packageInterval'], '').toLowerCase() || inferPassInterval(item);
  const priceSuffix = PASS_PRICE_SUFFIX[interval] || '';
  const priceLabel = appendPassPriceSuffix(price, priceSuffix);
  const originalPriceLabel = appendPassPriceSuffix(originalPrice, priceSuffix);
  const vipFallbackStyle = {
    tone: VIP_PASS_TONES[index % VIP_PASS_TONES.length],
    toneText: VIP_PASS_TONES_TEXT[index % VIP_PASS_TONES_TEXT.length],
    toneBorder: VIP_PASS_TONES_BORDER[index % VIP_PASS_TONES_BORDER.length],
  };
  const { headerGradient, tone, toneText, toneBorder } = resolvePassItemStyle(item, vipFallbackStyle);

  return {
    id: getIapId(item, index),
    passKind: 'vip',
    title,
    validity: buildPassValidity(item, pkg, validityDays, displayDuration),
    points:
      points ||
      pickString(item, ['points', 'benefitText']) ||
      (pkg.rapCoins != null ? `${pkg.rapCoins} Rap Coins` : pkg.coins ? `${pkg.coins} Rap Coins` : ''),
    gemsLabel: 'RapGems',
    gemsDetail,
    roomAccess,
    price,
    priceLabel,
    originalPrice: originalPriceLabel,
    headerGradient,
    tone,
    toneText,
    toneBorder,
    interval,
    productId: item?.androidStoreProductId,
    raw: item,
  };
}

function mapPassItem(item, index) {
  const iapType = getItemIapType(item);
  if (iapType === 'pass') {
    return mapGemPassItem(item, index);
  }

  const interval = pickString(item, ['interval', 'billingInterval', 'packageInterval'], '').toLowerCase();
  if (!iapType && GEM_PASS_INTERVALS.has(interval)) {
    return mapGemPassItem(item, index);
  }

  return mapVipPassItem(item, index);
}

const VIP_ICON_TYPES = [
  { match: /family/i, iconType: 'family', subtitleWidth: 66 },
  { match: /kitty|party/i, iconType: 'party', subtitleWidth: 80 },
  { match: /corporate/i, iconType: 'corporate', subtitleWidth: 78 },
];

function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

function getPackage(item) {
  return item?.package && typeof item.package === 'object' ? item.package : {};
}

function formatRupee(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    return value.trim().startsWith('₹') ? value.trim() : `₹${value.trim()}`;
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);

  return `₹${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
}

function appendPassPriceSuffix(price, suffix) {
  if (!price) return '';

  const label = String(price);
  if (!suffix || /\/(?:day|week|month|months)\b/i.test(label)) return label;

  return `${label}${suffix}`;
}

function pickNumber(item, keys) {
  for (const key of keys) {
    if (item?.[key] != null && item[key] !== '') return item[key];
  }
  return null;
}

function pickString(item, keys, fallback = '') {
  for (const key of keys) {
    if (item?.[key] != null && String(item[key]).trim()) return String(item[key]).trim();
  }
  return fallback;
}

function getIapId(item, index) {
  return item?._id ?? item?.id ?? item?.androidStoreProductId ?? index + 1;
}

function resolveIapPrices(item) {
  const hasDiscount = item?.discountedPrice != null && item.discountedPrice !== '';
  const listPrice = item?.displayPrice || formatRupee(item?.androidStorePrice ?? item?.price?.value);

  if (hasDiscount) {
    return {
      price: formatRupee(item.discountedPrice),
      originalPrice:
        item?.price?.value != null
          ? formatRupee(item.price.value)
          : listPrice || '',
    };
  }

  return {
    price: listPrice || formatRupee(item?.price?.value ?? item?.androidStorePrice),
    originalPrice: '',
  };
}

function extractIapPayload(body) {
  const data = body?.data ?? body;
  let items = [];

  if (Array.isArray(data?.iaps)) items = data.iaps;
  else if (Array.isArray(data)) items = data;
  else if (Array.isArray(data?.items)) items = data.items;
  else if (Array.isArray(data?.products)) items = data.products;
  else if (Array.isArray(data?.packages)) items = data.packages;
  else if (Array.isArray(data?.bundles)) items = data.bundles;
  else if (Array.isArray(data?.list)) items = data.list;
  else if (data && typeof data === 'object') {
    const typedItems = [];
    if (Array.isArray(data.pass)) typedItems.push(...data.pass);
    if (Array.isArray(data.subscriptionPackage)) typedItems.push(...data.subscriptionPackage);
    if (typedItems.length) items = typedItems;
  }

  items = items.filter((item) => {
    if (item?.isActive === false) return false;
    if (Array.isArray(item?.displayOn) && item.displayOn.length && !item.displayOn.includes('web')) {
      return false;
    }
    return true;
  });

  items.sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0));

  return {
    items,
    activeOffer: data?.activeOffer ?? null,
  };
}

function resolveVipIconMeta(label) {
  const match = VIP_ICON_TYPES.find((entry) => entry.match.test(label));
  return match || { iconType: 'family', subtitleWidth: 66 };
}

function mapRapCoinItem(item, index) {
  const asset = RAPCOIN_ASSETS[index % RAPCOIN_ASSETS.length];
  const pkg = getPackage(item);
  const amount =
    pkg.coins ??
    pkg.rapCoins ??
    pkg.coin ??
    pickNumber(item, ['coins', 'rapCoins', 'coinAmount', 'quantity']) ??
    pickString(item, ['displayName', 'title', 'name'], '').match(/\d+/)?.[0] ??
    '';

  const { price, originalPrice } = resolveIapPrices(item);

  return {
    id: getIapId(item, index),
    title: String(amount),
    subtitle: 'RapCoins',
    price,
    originalPrice,
    tone: 'coin',
    ...asset,
    productId: item?.androidStoreProductId,
    raw: item,
  };
}

function mapRapGemItem(item, index) {
  const pkg = getPackage(item);
  const boosts = pkg.gems ?? null;
  const flips = pkg.flip ?? pkg.swap ?? null;
  const isMix = boosts != null && flips != null;
  const { price, originalPrice } = resolveIapPrices(item);

  if (isMix) {
    return {
      id: getIapId(item, index),
      title: String(boosts).padStart(2, '0'),
      subtitle: 'RapGems',
      secondaryTitle: String(flips).padStart(2, '0'),
      secondarySubtitle: 'RapGems',
      price,
      originalPrice,
      tone: 'gem-mix',
      gemType: 'mix',
      productId: item?.androidStoreProductId,
      raw: item,
    };
  }

  const isFlip = flips != null && boosts == null;
  const count = boosts ?? flips ?? '';

  return {
    id: getIapId(item, index),
    title: String(count).padStart(2, '0'),
    subtitle: 'RapGems',
    price,
    originalPrice,
    tone: isFlip ? 'gem-blue' : 'gem-red',
    gemType: 'gem',
    productId: item?.androidStoreProductId,
    raw: item,
  };
}

function mapVipRoomItem(item, index) {
  const displayName = pickString(item, ['displayName', 'description', 'roomName', 'name', 'title'], 'VIP Room');
  const { iconType, subtitleWidth } = resolveVipIconMeta(displayName);
  const pkg = getPackage(item);
  const count =
    pkg.room ??
    pkg.rooms[0]['roomCount'] ??
    pkg.roomCount ??
    pickNumber(item, ['rooms', 'quantity', 'count']) ??
    1;
  const { price, originalPrice } = resolveIapPrices(item);

  return {
    id: getIapId(item, index),
    title: String(count).padStart(2, '0'),
    subtitle: displayName,
    price,
    originalPrice,
    tone: 'vip',
    iconType,
    subtitleWidth,
    productId: item?.androidStoreProductId,
    raw: item,
  };
}

export function computeMartRewardProgress(xpValue, profileXp = 0) {
  const cost = Number(xpValue) || 0;
  const xp = Number(profileXp) || 0;
  if (cost <= 0) return '0%';
  return `${Math.min(100, Math.round((xp / cost) * 100))}%`;
}

function mapMartRewardItem(item, profileXp = 0) {
  const xpValue = Number(item?.xpValue) || 0;
  const progress = computeMartRewardProgress(xpValue, profileXp);

  return {
    id: item?.id ?? item?._id,
    rewardId: item?.id ?? item?._id,
    title: pickString(item, ['title'], 'Reward'),
    subtitle: pickString(item, ['description'], ''),
    brand: pickString(item, ['displayName', 'source'], 'Voucher'),
    cost: formatXpCost(xpValue),
    xpValue,
    imageUrl: pickString(item, ['bannerUrl'], ''),
    imageTone: SOURCE_IMAGE_TONE[item?.source] || 'fashion',
    progress,
    raw: item,
  };
}

function mapIapItems(type, items) {
  if (type === BAZAAR_IAP_TYPES.rapcoin) return items.map(mapRapCoinItem);
  if (type === BAZAAR_IAP_TYPES.rapgem) return items.map(mapRapGemItem);
  if (type === BAZAAR_IAP_TYPES['vip-room']) return items.map(mapVipRoomItem);
  if (type === BAZAAR_IAP_TYPES.passes) return items.map(mapPassItem);
  return items;
}

export async function fetchMartHome() {
  const response = await axios_adapter.get(ENDPOINTS.RBZ_HOME);
  const data = response.data?.data ?? response.data ?? {};
  //const availableXP = 10000000000000000000000000; // Number(data.availableXP) || 0;
  const availableXP = Number(data.availableXP) || 0;
  const profileXp = getTotalXp();
  const xpExpirationLabel =
    pickString(data, ['xpExpirationLabel'], 'XPs have 7 day validity. Redeem Now!');

  const rewards = Array.isArray(data.xpRewards) ? data.xpRewards : [];
  const items = rewards
    .filter((item) => item?.status !== 'inactive')
    .map((item) => mapMartRewardItem(item, profileXp));

  return {
    availableXP,
    xpExpirationLabel,
    items,
  };
}

export const REDEEM_ERROR_COUPON_UNAVAILABLE = -4;

export function buildCouponUnavailableMessage(result) {
  const data = result ?? {};
  const base = data.message || 'This coupon is currently unavailable.';
  if (Number(data.xpRefunded) > 0 && !/refund/i.test(base)) {
    return `${base} The deducted XPs will be refunded in your wallet.`;
  }
  return base;
}

export function isCouponUnavailableError(result) {
  return Boolean(result) && result.ok === false && Number(result.errorCode) === REDEEM_ERROR_COUPON_UNAVAILABLE;
}

export async function redeemMartReward(rewardId) {
  if (!rewardId) {
    throw new Error('Unable to identify voucher for redemption.');
  }

  const response = await axios_adapter.post(ENDPOINTS.RBZ_REDEEM, {
    rewardId: String(rewardId),
  });

  const body = response.data;
  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to redeem voucher.');
  }

  const data = body?.data ?? body;
  if (data?.success === false) {
    return {
      ok: false,
      message: data?.message || 'Unable to redeem voucher.',
      xpRefunded: Number(data?.xpRefunded) || 0,
      errorCode: data?.errorCode ?? null,
    };
  }

  return { ok: true, ...data };
}

export function extractRedemptionCoupon(data = {}) {
  const nested =
    data?.coupon && typeof data.coupon === 'object'
      ? data.coupon.code ?? data.coupon.couponCode ?? data.coupon.value
      : null;

  const code =
    data?.couponCode ??
    nested ??
    (typeof data?.coupon === 'string' ? data.coupon : null) ??
    data?.code ??
    data?.voucherCode ??
    data?.redemptionCode ??
    data?.rewardCode ??
    '';

  return String(code).trim();
}

export function isUrlCouponReward(coupon) {
  const couponType = String(coupon?.couponType || coupon?.raw?.couponType || '')
    .trim()
    .toUpperCase();
  const code = extractRedemptionCoupon(coupon);
  return couponType === 'URL' || /^https?:\/\//i.test(code);
}

export function openRewardCouponInNewTab(coupon) {
  const url = extractRedemptionCoupon(coupon);
  if (!url || !isUrlCouponReward(coupon)) return false;

  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

function formatRewardTabDateLabel(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string' && /\d{1,2}(st|nd|rd|th)\s/i.test(value)) {
    return value.trim();
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).trim();

  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  const year = String(date.getFullYear()).slice(-2);

  return `${day}${suffix} ${month} ${year}`;
}

function formatRewardTimerLabel(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string' && value.includes(':')) {
    return value.trim();
  }

  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return String(value).trim();

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(' : ');
}

function resolveRapchikRewardTheme(item = {}) {
  const type = pickString(item, ['theme', 'cardTheme', 'type', 'rewardType', 'category'], '').toLowerCase();
  const title = pickString(item, ['title', 'name'], '').toLowerCase();

  if (type.includes('welcome') || title.includes('welcome')) return 'welcome';
  if (type.includes('game') && type.includes('time')) return 'gameTime';
  if (type.includes('gametime') || title.includes('game time')) return 'gameTime';
  if (type.includes('streak') || title.includes('streak')) return 'dailyStreak';
  if (type.includes('daily') && type.includes('score')) return 'dailyScore';
  if (type.includes('score') || title.includes('score')) return 'dailyScore';

  return 'welcome';
}

function resolveRapchikLeftVariant(item = {}, theme) {
  const variant = pickString(item, ['leftVariant', 'leftType', 'metricType'], '').toLowerCase();

  if (variant.includes('wheel') || variant.includes('rapwheel')) return 'wheel';
  if (variant.includes('timer') || variant.includes('countdown') || variant.includes('clock')) return 'timer';
  if (variant.includes('streak')) return 'streak';
  if (variant.includes('score')) return 'score';

  if (theme === 'gameTime') return 'timer';
  if (theme === 'dailyStreak') return 'streak';
  if (theme === 'dailyScore') return 'score';
  if (theme === 'welcome') return 'wheel';

  return 'score';
}

function mapRapchikRewardItem(item, index) {
  const theme = resolveRapchikRewardTheme(item);
  const leftVariant = resolveRapchikLeftVariant(item, theme);
  const rank = pickNumber(item, ['rank', 'medalRank', 'position']) ?? null;
  const prizeAmount =
    pickNumber(item, ['prizeAmount', 'amount', 'rupees', 'prizeValue', 'rewardAmount']) ?? null;
  const timerValue =
    item?.timer ??
    item?.countdown ??
    item?.remainingSeconds ??
    item?.timeRemaining ??
    item?.leftValue;
  const showRevealPrize =
    item?.showRevealPrize === true ||
    item?.canRevealPrize === true ||
    item?.revealPrize === true ||
    (theme === 'gameTime' && item?.showRevealPrize !== false && item?.canRevealPrize !== false);

  return {
    id: item?.id ?? item?._id ?? item?.rewardId ?? `rapchik-reward-${index}`,
    theme,
    title: pickString(item, ['title', 'name', 'rewardTitle'], 'Reward'),
    dateLabel: formatRewardTabDateLabel(
      item?.dateLabel ?? item?.date ?? item?.eventDate ?? item?.rewardDate ?? item?.createdAt,
    ),
    rank: rank && rank >= 1 && rank <= 3 ? rank : undefined,
    leftVariant,
    leftLabel: pickString(item, ['leftLabel', 'sourceLabel', 'gameName'], 'RapWheel'),
    leftValue:
      leftVariant === 'timer'
        ? formatRewardTimerLabel(timerValue)
        : pickString(
            item,
            ['leftValue', 'score', 'streak', 'metricValue', 'value'],
            leftVariant === 'score' ? '0' : '',
          ),
    prizeAmount,
    prizeCurrency: pickString(item, ['prizeCurrency', 'currency'], 'Rupees'),
    showRevealPrize: Boolean(showRevealPrize),
    raw: item,
  };
}

function extractRapchikRewardsPayload(body) {
  const data = body?.data ?? body;

  const totalWinnings =
    pickNumber(data, ['totalWinnings', 'totalWinningsWorth', 'totalWinningWorth', 'winningsWorth']) ??
    pickNumber(data, ['totalAmount', 'totalRupees']) ??
    0;

  const list =
    (Array.isArray(data?.rewards) && data.rewards) ||
    (Array.isArray(data?.items) && data.items) ||
    (Array.isArray(data?.leaderboards) && data.leaderboards) ||
    (Array.isArray(data?.list) && data.list) ||
    (Array.isArray(data) && data) ||
    [];

  return {
    totalWinnings,
    items: list.map(mapRapchikRewardItem),
  };
}

export async function fetchRapchikRewards() {
  const response = await axios_adapter.get(ENDPOINTS.HOME_MY_REWARDS);
  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to load Rapchik rewards.');
  }

  return extractRapchikRewardsPayload(body);
}

function formatRapchikCouponCode(code) {
  const trimmed = String(code ?? '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function mapRapchikRewardDetail(item, index = 0) {
  const base = mapRapchikRewardItem(item, index);
  const howToUseSteps = Array.isArray(item?.howToUseSteps)
    ? item.howToUseSteps
    : Array.isArray(item?.howToUse)
      ? item.howToUse
      : Array.isArray(item?.instructions)
        ? item.instructions
        : [];
  const rawCouponCode =
    extractRedemptionCoupon(item) || pickString(item, ['couponCode', 'code', 'voucherCode'], '');
  const couponCode = formatRapchikCouponCode(rawCouponCode);
  const couponType = pickString(item, ['couponType'], '').toUpperCase();
  const isExpired = Boolean(item?.isExpired) || item?.status === 'expired';
  const brand = pickString(item, ['brand', 'source', 'merchant'], 'Amazon');
  const prizeAmount = base.prizeAmount;
  const winMessage =
    pickString(item, ['winMessage', 'prizeMessage', 'subtitle', 'description'], '') ||
    (prizeAmount != null
      ? `You won an ${brand} coupon worth ₹${prizeAmount}`
      : pickString(item, ['title', 'name'], 'You won a reward'));

  return {
    ...base,
    winMessage,
    couponCode,
    couponType,
    isExpired,
    canAccessCoupon: Boolean(rawCouponCode) && !isExpired,
    imageUrl: pickString(item, ['imageUrl', 'bannerUrl', 'prizeImageUrl'], ''),
    howToUseSteps,
  };
}

function extractRapchikRewardRecord(body) {
  const data = body?.data ?? body;

  if (data?.reward && typeof data.reward === 'object') return data.reward;
  if (data?.prize && typeof data.prize === 'object') return data.prize;
  if (data?.id || data?._id || data?.rewardId) return data;
  if (data && typeof data === 'object' && !Array.isArray(data)) return data;

  return null;
}

export async function fetchRapchikRewardById(id) {
  if (!id) return null;

  try {
    const response = await axios_adapter.get(ENDPOINTS.homeMyRewardById(id));
    const body = response.data;

    if (body?.responseCode && body.responseCode !== 200) {
      throw new Error(body?.data?.message || body?.message || 'Unable to load reward.');
    }

    const record = extractRapchikRewardRecord(body);
    if (record) return mapRapchikRewardDetail(record);
  } catch {
    // Fall back to list lookup when detail endpoint is unavailable.
  }

  const { items } = await fetchRapchikRewards();
  const match = items.find((item) => String(item.id) === String(id));
  return match ? mapRapchikRewardDetail(match.raw ?? match, 0) : null;
}

function mapRedeemedCouponItem(item, index) {
  const { brand, imageTone } = inferRewardMeta(item);
  const howToUseSteps = Array.isArray(item?.howToUseSteps)
    ? item.howToUseSteps
    : Array.isArray(item?.howToUse)
      ? item.howToUse
      : [];
  const couponCode = extractRedemptionCoupon(item);
  const couponType = pickString(item, ['couponType'], '').toUpperCase();
  const isExpired = Boolean(item?.isExpired) || item?.status === 'expired';
  const xpValue = item?.xpRedeemed ?? item?.redeemedXp;

  return {
    id: item?.allotmentId ?? item?.id ?? item?._id ?? index,
    title: pickString(item, ['title', 'rewardTitle', 'name'], 'Reward'),
    subtitle: pickString(item, ['description', 'subtitle'], ''),
    brand,
    imageUrl: pickString(item, ['bannerUrl', 'imageUrl', 'banner']),
    imageTone,
    validTill: formatValidTillLabel(
      item?.validTill ?? item?.validUntil ?? item?.expiryDate ?? item?.expiresAt ?? item?.expiry,
    ),
    couponCode,
    couponType,
    isExpired,
    status: pickString(item, ['status'], ''),
    canAccessCoupon: Boolean(couponCode) && !isExpired,
    redeemedXp: xpValue != null && xpValue !== '' ? formatXpCost(xpValue) : '',
    redeemedAt: formatRedeemedAtLabel(item?.redeemedAt ?? item?.redeemedOn),
    howToUseSteps,
    raw: item,
  };
}

function extractRedeemedCouponsList(body) {
  const data = body?.data ?? body;

  if (Array.isArray(data?.rewards)) return data.rewards;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.coupons)) return data.coupons;
  if (Array.isArray(data?.redeemedCoupons)) return data.redeemedCoupons;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;

  return [];
}

function extractRedeemedCouponRecord(body) {
  const data = body?.data ?? body;

  if (data?.reward && typeof data.reward === 'object') return data.reward;
  if (data?.coupon && typeof data.coupon === 'object') return data.coupon;
  if (data?.redeemedCoupon && typeof data.redeemedCoupon === 'object') return data.redeemedCoupon;
  if (data?.allotmentId) return data;
  if (data && typeof data === 'object' && !Array.isArray(data)) return data;

  return null;
}

export async function fetchRedeemedCoupons() {
  const response = await axios_adapter.get(ENDPOINTS.RBZ_MY_REWARDS);
  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to load redeemed coupons.');
  }

  return extractRedeemedCouponsList(body).map(mapRedeemedCouponItem);
}

export async function fetchRedeemedCouponById(id) {
  if (!id) return null;

  try {
    const response = await axios_adapter.get(ENDPOINTS.rbzMyRewardById(id));
    const body = response.data;

    if (body?.responseCode && body.responseCode !== 200) {
      throw new Error(body?.data?.message || body?.message || 'Unable to load coupon.');
    }

    const record = extractRedeemedCouponRecord(body);
    if (record) return mapRedeemedCouponItem(record);
  } catch {
    // Fall back to list lookup when detail endpoint is unavailable.
  }

  const items = await fetchRedeemedCoupons();
  return items.find((item) => String(item.id) === String(id)) ?? null;
}

export async function fetchBazaarIaps(type) {
  const response = await axios_adapter.get(ENDPOINTS.RBZ_IAPS_WEB, {
    params: { type },
  });

  const { items, activeOffer } = extractIapPayload(response.data);

  return {
    items: mapIapItems(type, items),
    activeOffer,
  };
}

export async function fetchPassCatalog() {
  const response = await axios_adapter.get(ENDPOINTS.RBZ_IAPS_WEB, {
    params: { type: BAZAAR_IAP_TYPES.passes },
  });

  const { items: rawItems, activeOffer } = extractIapPayload(response.data);
  const passRawItems = rawItems.filter((item) => getItemIapType(item) === 'pass');
  const vipRawItems = rawItems.filter((item) => getItemIapType(item) !== 'pass');

  return {
    gemPasses: passRawItems.map((item, index) => mapGemPassItem(item, index)),
    vipPasses: vipRawItems.map((item, index) => mapVipPassItem(item, index)),
    activeOffer,
  };
}

export async function fetchAboutXpContent(slug, options = {}) {
  if (!slug) return null;

  const response = await axios_adapter.get(ENDPOINTS.userAboutXp(slug), {
    signal: options.signal,
  });

  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to load content.');
  }

  return body?.data ?? body;
}

export { getApiErrorMessage };
